import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DollarSign, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  useFinancialIntegrityChecks,
  FinancialIntegrityCheck,
} from '@/hooks/useFinancialIntegrityChecks';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

function severityBadge(check: FinancialIntegrityCheck) {
  if (check.severity === 'critical') {
    return (
      <Badge variant="destructive">
        <XCircle className="h-3 w-3 mr-1" />
        Critical
      </Badge>
    );
  }
  if (check.severity === 'warning') {
    return (
      <Badge variant="warning">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Warning
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Info
    </Badge>
  );
}

export function FinancialIntegrityCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useFinancialIntegrityChecks();
  const { formatMoney, locale } = useCompanyLocale();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['financial-integrity-checks'] });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Financial Integrity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">Running checks...</div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Financial Integrity
          </CardTitle>
          <CardDescription>
            Checks unavailable — has the run_financial_integrity_checks migration been applied?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to run financial integrity checks'}
          </div>
        </CardContent>
      </Card>
    );
  }

  const failing = data.checks.filter((c) => c.affected > 0);
  const issueCount = data.critical_count + data.warning_count;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Financial Integrity
          </CardTitle>
          <div className="flex items-center gap-2">
            {issueCount === 0 ? (
              <Badge variant="success">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Healthy
              </Badge>
            ) : data.critical_count > 0 ? (
              <Badge variant="destructive">
                <XCircle className="h-3 w-3 mr-1" />
                {issueCount} issue{issueCount === 1 ? '' : 's'}
              </Badge>
            ) : (
              <Badge variant="warning">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {issueCount} issue{issueCount === 1 ? '' : 's'}
              </Badge>
            )}
            <Button variant="ghost" size="icon" onClick={handleRefresh} title="Re-run checks">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          Money data reconciliation: jobs, payments, invoices, commissions, campaigns
        </CardDescription>
      </CardHeader>
      <CardContent>
        {failing.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            All {data.checks.length} checks passed. Last run:{' '}
            {new Date(data.generated_at).toLocaleString(locale.locale)}
          </div>
        ) : (
          <div className="space-y-3">
            {failing.map((check) => (
              <div key={check.key} className="rounded-md border p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{check.label}</span>
                  {severityBadge(check)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {check.affected} record{check.affected === 1 ? '' : 's'} affected
                  {check.amount > 0 && <> · {formatMoney(check.amount)}</>}
                </div>
                {check.samples.length > 0 && (
                  <div className="text-xs font-mono text-muted-foreground truncate">
                    e.g. {check.samples.join(', ')}
                  </div>
                )}
              </div>
            ))}
            <div className="text-xs text-muted-foreground">
              Last run: {new Date(data.generated_at).toLocaleString(locale.locale)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
